import React, { useCallback, useEffect, useState } from "react";
import {
  Factory, Sparkles, ListChecks, CheckCircle2, ClipboardCheck, Package, Boxes,
  PlayCircle, FlaskConical, ShieldCheck, Printer, Clock, ArrowRight, AlertTriangle, Check, X,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, Modal, Input, PageHeader, PageLoader } from "../lib/ui";
import { fmtNum, fmtRp } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import OpsProgress from "../components/OpsProgress";
import { usePrintDoc } from "../lib/usePrintDoc";

const PHASES = ["Mixing", "Cooking", "Cooling", "Filling", "Done"];

const STAGE_TONE = {
  WAITING: "mute", MATERIAL_VALIDATION: "warn", PRODUCTION: "info",
  QC: "warn", PACKING: "info", READY: "success", COMPLETED: "success",
};
const STAGE_LABEL = {
  WAITING: "Waiting", MATERIAL_VALIDATION: "Material Validation", PRODUCTION: "Production",
  QC: "Quality Control", PACKING: "Packing", READY: "Ready for Shipment", COMPLETED: "Completed",
};

function Step({ n, title, icon: Icon, done, children, testid, count }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-3.5">
        <span className={`flex h-8 w-8 items-center justify-center rounded-xl text-sm font-extrabold ${done ? "bg-emerald-500 text-white" : "bg-primary-light text-primary"}`}>
          {done ? <CheckCircle2 size={18} /> : n}
        </span>
        <Icon size={18} className="text-ink-soft" />
        <h3 className="font-heading text-base font-extrabold text-ink">{title}</h3>
        {count != null && <Badge tone="mute" className="ml-auto">{count}</Badge>}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function Kpi({ label, value, sub, tone = "ink", testid }) {
  const toneCls = { ink: "text-ink", primary: "text-primary", emerald: "text-emerald-600", amber: "text-amber-600", blue: "text-blue-600" }[tone];
  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-ink-mute">{label}</p>
      <p className={`font-heading text-2xl font-extrabold ${toneCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-ink-mute">{sub}</p>}
    </div>
  );
}

export function ProductionWorkflow({ embedded = false }) {
  const { t } = useT();
  const toast = useToast();
  const { printDoc, printing } = usePrintDoc();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [recQty, setRecQty] = useState({});
  const [modal, setModal] = useState(null);          // { type, batch }
  const [materials, setMaterials] = useState(null);
  const [resForm, setResForm] = useState({ actual: "", waste: "0", notes: "" });
  const [qcState, setQcState] = useState({});         // {key: bool}
  const [qcNotes, setQcNotes] = useState("");
  const [packForm, setPackForm] = useState({ label: "", count: "", expiry: "", notes: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/production/workspace"); setData(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); await load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || t("Failed")));
    } finally { setBusy(null); }
  };

  const createBatch = (rec) => {
    const qty = parseFloat(recQty[rec.product_id] ?? rec.recommended_qty);
    if (!qty || qty <= 0) { toast.error(t("Enter a quantity")); return; }
    const multiplier = qty / (rec.output_per_batch || 1);
    act(async () => {
      await api.post("/production/batches", { recipe_id: rec.recipe_id, multiplier });
      toast.success(`${rec.product_name} · ${t("added to queue")}`);
    }, `rec-${rec.product_id}`);
  };

  const openMaterial = async (batch) => {
    setModal({ type: "material", batch });
    setMaterials(null);
    try { const { data } = await api.get(`/production/batches/${batch.id}/material-check`); setMaterials(data); }
    catch (e) { toast.error(t("Failed")); }
  };
  const startProduction = (batch) => act(async () => {
    await api.post(`/production/batches/${batch.id}/validate`, {});
    await api.post(`/production/batches/${batch.id}/start`, {});
    toast.success(t("Production started")); setModal(null);
  }, "start");

  const advancePhase = (batch) => {
    const idx = PHASES.indexOf(batch.production_phase || PHASES[0]);
    const next = PHASES[Math.min(idx + 1, PHASES.length - 1)];
    act(async () => { await api.post(`/production/batches/${batch.id}/progress`, { phase: next }); }, `phase-${batch.id}`);
  };

  const openResult = (batch) => { setResForm({ actual: String(batch.planned_output_qty ?? ""), waste: "0", notes: "" }); setModal({ type: "result", batch }); };
  const submitResult = (batch) => act(async () => {
    await api.post(`/production/batches/${batch.id}/result`, {
      actual_output_qty: parseFloat(resForm.actual), production_waste: parseFloat(resForm.waste || 0), notes: resForm.notes });
    toast.success(t("Result recorded · sent to QC")); setModal(null);
  }, "result");

  const openQC = (batch) => {
    const init = {}; (data.qc_default_items || []).forEach((i) => { init[i.key] = true; });
    setQcState(init); setQcNotes(""); setModal({ type: "qc", batch });
  };
  const submitQC = (batch) => {
    const items = (data.qc_default_items || []).map((i) => ({ key: i.key, label: i.label, passed: !!qcState[i.key] }));
    const passed = items.every((i) => i.passed);
    act(async () => {
      await api.post(`/production/batches/${batch.id}/qc`, { items, passed, notes: qcNotes });
      toast[passed ? "success" : "error"](passed ? t("QC passed · ready to pack") : t("QC failed · inventory stays pending"));
      setModal(null);
    }, "qc");
  };

  const openPacking = (batch) => { setPackForm({ label: "", count: "", expiry: "", notes: "" }); setModal({ type: "packing", batch }); };
  const submitPacking = (batch) => act(async () => {
    const { data: r } = await api.post(`/production/batches/${batch.id}/packing`, {
      package_label: packForm.label || null, package_count: packForm.count ? parseFloat(packForm.count) : null,
      expiry_date: packForm.expiry || null, notes: packForm.notes || null });
    toast.success(`${t("Packed")} · ${r.batch_number}`); setModal(null);
  }, "packing");

  const printLabel = (batch) => printDoc(`/production/batches/${batch.id}/label/pdf`, { key: `label-${batch.id}`, label: t("Label") });

  if (!data) return <PageLoader />;
  const s = data.owner_summary;
  const recs = data.recommendations || [];
  const queue = data.queue || [];
  const completed = (data.today_batches || []).filter((b) => b.status === "CONFIRMED");

  const primaryAction = (b) => {
    const stage = b.workflow_stage;
    if (stage === "WAITING" || stage === "MATERIAL_VALIDATION")
      return <Button size="sm" onClick={() => openMaterial(b)} data-testid={`cp-validate-${b.id}`}><ClipboardCheck size={15} /> {t("Validate & Start")}</Button>;
    if (stage === "PRODUCTION")
      return <Button size="sm" onClick={() => openResult(b)} data-testid={`cp-result-${b.id}`}><FlaskConical size={15} /> {t("Record Result")}</Button>;
    if (stage === "QC")
      return <Button size="sm" onClick={() => openQC(b)} data-testid={`cp-qc-${b.id}`}><ShieldCheck size={15} /> {t("Quality Control")}</Button>;
    if (stage === "PACKING")
      return <Button size="sm" onClick={() => openPacking(b)} data-testid={`cp-packing-${b.id}`}><Package size={15} /> {t("Packing")}</Button>;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="central-production-page">
      {!embedded && (
        <PageHeader title={t("Central Production")} subtitle={t("Auto-recommended production, guided workflow, QC & packing — powered by the existing engines.")} icon={Factory} />
      )}

      {/* Owner Summary */}
      <Card className="p-5" data-testid="cp-owner-summary">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-base font-extrabold text-ink">{t("Owner Summary")}</h3>
          <Badge tone="info" data-testid="cp-est-finish"><Clock size={13} /> {t("Est. Finish")}: {s.est_finish || "—"}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Kpi label={t("Today's Target")} value={fmtNum(s.target_qty)} tone="ink" testid="cp-kpi-target" />
          <Kpi label={t("Completed")} value={fmtNum(s.completed_qty)} sub={`${s.completed_count} ${t("batches")}`} tone="emerald" testid="cp-kpi-completed" />
          <Kpi label={t("Remaining")} value={fmtNum(s.remaining_qty)} tone="amber" testid="cp-kpi-remaining" />
          <Kpi label={t("In Production")} value={fmtNum(s.in_production_qty)} tone="blue" testid="cp-kpi-inprod" />
          <Kpi label={t("QC Waiting")} value={fmtNum(s.qc_waiting_qty)} tone="amber" testid="cp-kpi-qc" />
          <Kpi label={t("Ready for Shipment")} value={fmtNum(s.ready_for_shipment_qty)} tone="emerald" testid="cp-kpi-ready" />
        </div>
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-xs text-ink-mute"><span>{t("Completion")}</span><span>{s.completion_pct}%</span></div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-line"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, s.completion_pct)}%` }} /></div>
        </div>
      </Card>

      <OpsProgress items={data.workflow_steps} testid="cp-progress" />

      {/* Step 1 — Production Recommendation */}
      <Step n={1} title={t("Production Recommendation")} icon={Sparkles} done={recs.length === 0 && queue.length > 0} testid="cp-step-recommendation">
        <p className="mb-3 text-xs text-ink-mute">{t("Rule-based: Branch Demand + Minimum Stock − Current Stock − Open Production. Edit qty before starting.")}</p>
        {recs.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-mute" data-testid="cp-no-recs">{t("Central stock is healthy — no production needed right now.")}</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="border-b border-line bg-primary-light/40 text-left text-xs text-primary">
                <th className="px-3 py-2 font-bold">{t("Product")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Branch Demand")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Min")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Stock")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Open")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Recommended")}</th>
                <th className="px-3 py-2 text-right font-bold">{t("Produce")}</th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {recs.map((r) => (
                  <tr key={r.product_id} className="hover:bg-primary-light/20" data-testid={`cp-rec-${r.product_id}`}>
                    <td className="px-3 py-2 font-medium text-ink">{r.product_name} <span className="text-[11px] text-ink-mute">{r.unit}</span></td>
                    <td className="px-3 py-2 text-right text-ink-soft">{fmtNum(r.branch_demand)}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{fmtNum(r.minimum_stock)}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{fmtNum(r.current_stock)}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{fmtNum(r.open_production)}</td>
                    <td className="px-3 py-2 text-right"><span className="font-heading font-extrabold text-primary">{fmtNum(r.recommended_qty)}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <input type="number" className="w-20 rounded-lg border border-line px-2 py-1 text-right text-sm" min="0"
                          value={recQty[r.product_id] ?? r.recommended_qty}
                          onChange={(e) => setRecQty((p) => ({ ...p, [r.product_id]: e.target.value }))}
                          data-testid={`cp-rec-qty-${r.product_id}`} />
                        <Button size="sm" loading={busy === `rec-${r.product_id}`} onClick={() => createBatch(r)} data-testid={`cp-rec-start-${r.product_id}`}>{t("Add to Queue")}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Step>

      {/* Step 2 — Today's Production Queue */}
      <Step n={2} title={t("Today's Production Queue")} icon={ListChecks} done={queue.length === 0 && completed.length > 0} testid="cp-step-queue" count={queue.length}>
        {queue.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-mute" data-testid="cp-queue-empty">{t("No active production. Add items from the recommendation above.")}</p>
        ) : (
          <div className="space-y-3">
            {queue.map((b) => (
              <div key={b.id} className="rounded-xl border border-line p-4" data-testid={`cp-queue-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Boxes size={16} className="text-primary" />
                  <p className="font-heading text-base font-extrabold text-ink">{b.product_name}</p>
                  <Badge tone={STAGE_TONE[b.workflow_stage]} data-testid={`cp-stage-${b.id}`}>{t(STAGE_LABEL[b.workflow_stage])}</Badge>
                  <span className="text-xs text-ink-mute">{b.production_no} · {t("Plan")} {fmtNum(b.planned_output_qty)} {b.unit}</span>
                  {b.assigned_to && <Badge tone="mute">PIC: {b.assigned_to}</Badge>}
                  {b.est_finish && <Badge tone="mute"><Clock size={12} /> {b.est_finish}</Badge>}
                  <div className="ml-auto flex items-center gap-2">{primaryAction(b)}</div>
                </div>
                {b.workflow_stage === "PRODUCTION" && (
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-xs text-ink-mute">
                      <span>{t("Phase")}: <b className="text-ink">{b.production_phase || "—"}</b></span>
                      <div className="flex items-center gap-2">
                        <span>{b.production_progress || 0}%</span>
                        <Button size="sm" variant="outline" loading={busy === `phase-${b.id}`} onClick={() => advancePhase(b)} data-testid={`cp-phase-${b.id}`}>{t("Next Phase")} <ArrowRight size={13} /></Button>
                      </div>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-line"><div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${b.production_progress || 0}%` }} /></div>
                  </div>
                )}
                {b.workflow_stage === "QC" && (b.qc?.status === "FAILED") && (
                  <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-rose-600"><AlertTriangle size={13} /> {t("QC failed — re-check and resubmit. Inventory pending.")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Step>

      {/* Step 3 — Completed Today (Batch info + label) */}
      <Step n={3} title={t("Completed Today")} icon={Package} done={completed.length > 0} testid="cp-step-completed" count={completed.length}>
        {completed.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-mute">{t("No completed production yet today.")}</p>
        ) : (
          <div className="space-y-2">
            {completed.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-emerald-50/40 p-3" data-testid={`cp-completed-${b.id}`}>
                <CheckCircle2 size={16} className="text-emerald-600" />
                <p className="font-bold text-ink">{b.product_name}</p>
                <Badge tone="success">{fmtNum(b.actual_output_qty)} {b.unit}</Badge>
                {b.batch_number && <Badge tone="mute">{t("Batch")}: {b.batch_number}</Badge>}
                {b.expiry_date && <Badge tone="warn">{t("Exp")}: {b.expiry_date}</Badge>}
                {b.yield_pct != null && <span className="text-xs text-ink-mute">{t("Yield")} {b.yield_pct}%</span>}
                <Button size="sm" variant="outline" className="ml-auto" loading={printing === `label-${b.id}`} onClick={() => printLabel(b)} data-testid={`cp-label-${b.id}`}><Printer size={14} /> {t("Print Label")}</Button>
              </div>
            ))}
          </div>
        )}
      </Step>

      {/* ---------- Modals ---------- */}
      <Modal open={modal?.type === "material"} onClose={() => setModal(null)} title={t("Raw Material Validation")}
        footer={<>
          <Button variant="ghost" onClick={() => setModal(null)}>{t("Cancel")}</Button>
          <Button disabled={!materials?.all_sufficient} loading={busy === "start"} onClick={() => startProduction(modal.batch)} data-testid="cp-start-confirm"><PlayCircle size={16} /> {t("Start Production")}</Button>
        </>}>
        {!materials ? <PageLoader /> : (
          <div className="space-y-2">
            <p className="text-xs text-ink-mute">{modal?.batch?.product_name} · {modal?.batch?.production_no}</p>
            {materials.materials.map((m) => (
              <div key={m.product_id} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2" data-testid={`cp-mat-${m.product_id}`}>
                {m.sufficient ? <Check size={16} className="text-emerald-600" /> : <X size={16} className="text-rose-600" />}
                <span className="flex-1 text-sm text-ink">{m.product_name}</span>
                <span className={`text-sm ${m.sufficient ? "text-ink-soft" : "font-bold text-rose-600"}`}>{fmtNum(m.available)} / {fmtNum(m.required)} {m.unit}</span>
              </div>
            ))}
            {!materials.all_sufficient && <p className="flex items-center gap-1 text-xs font-semibold text-rose-600"><AlertTriangle size={13} /> {t("Insufficient materials — production cannot start.")}</p>}
          </div>
        )}
      </Modal>

      <Modal open={modal?.type === "result"} onClose={() => setModal(null)} title={t("Actual Production Result")}
        footer={<><Button variant="ghost" onClick={() => setModal(null)}>{t("Cancel")}</Button>
          <Button loading={busy === "result"} onClick={() => submitResult(modal.batch)} data-testid="cp-result-submit">{t("Submit to QC")}</Button></>}>
        <div className="space-y-3">
          <p className="text-xs text-ink-mute">{modal?.batch?.product_name} · {t("Plan")} {fmtNum(modal?.batch?.planned_output_qty)} {modal?.batch?.unit}</p>
          <Input label={t("Actual Output")} type="number" value={resForm.actual} onChange={(e) => setResForm((p) => ({ ...p, actual: e.target.value }))} data-testid="cp-actual-output" />
          <Input label={t("Waste")} type="number" value={resForm.waste} onChange={(e) => setResForm((p) => ({ ...p, waste: e.target.value }))} data-testid="cp-waste" />
          <Input label={t("Notes") + " (" + t("optional") + ")"} value={resForm.notes} onChange={(e) => setResForm((p) => ({ ...p, notes: e.target.value }))} />
          <p className="text-[11px] text-ink-mute">{t("Yield, costing & inventory are auto-calculated by the engine after QC & packing.")}</p>
        </div>
      </Modal>

      <Modal open={modal?.type === "qc"} onClose={() => setModal(null)} title={t("Quality Control")}
        footer={<><Button variant="ghost" onClick={() => setModal(null)}>{t("Cancel")}</Button>
          <Button loading={busy === "qc"} onClick={() => submitQC(modal.batch)} data-testid="cp-qc-submit">{t("Submit QC")}</Button></>}>
        <div className="space-y-2">
          <p className="text-xs text-ink-mute">{t("Tick each passing check. If any fails, inventory stays pending.")}</p>
          {(data.qc_default_items || []).map((i) => (
            <label key={i.key} className="flex cursor-pointer items-center gap-3 rounded-lg border border-line px-3 py-2" data-testid={`cp-qc-item-${i.key}`}>
              <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={!!qcState[i.key]} onChange={(e) => setQcState((p) => ({ ...p, [i.key]: e.target.checked }))} />
              <span className="text-sm text-ink">{t(i.label)}</span>
            </label>
          ))}
          <Input label={t("Notes") + " (" + t("optional") + ")"} value={qcNotes} onChange={(e) => setQcNotes(e.target.value)} />
        </div>
      </Modal>

      <Modal open={modal?.type === "packing"} onClose={() => setModal(null)} title={t("Packing")}
        footer={<><Button variant="ghost" onClick={() => setModal(null)}>{t("Cancel")}</Button>
          <Button loading={busy === "packing"} onClick={() => submitPacking(modal.batch)} data-testid="cp-packing-submit"><Package size={16} /> {t("Complete Packing")}</Button></>}>
        <div className="space-y-3">
          <p className="text-xs text-ink-mute">{modal?.batch?.product_name} · {fmtNum(modal?.batch?.actual_output_qty)} {modal?.batch?.unit}</p>
          <Input label={t("Package Label") + " (" + t("optional") + ")"} placeholder="Pouch 500gr" value={packForm.label} onChange={(e) => setPackForm((p) => ({ ...p, label: e.target.value }))} data-testid="cp-pack-label" />
          <Input label={t("Package Count") + " (" + t("optional") + ")"} type="number" value={packForm.count} onChange={(e) => setPackForm((p) => ({ ...p, count: e.target.value }))} />
          <Input label={t("Expiry Date") + " (" + t("auto if shelf-life set") + ")"} type="date" value={packForm.expiry} onChange={(e) => setPackForm((p) => ({ ...p, expiry: e.target.value }))} data-testid="cp-pack-expiry" />
          <p className="text-[11px] text-ink-mute">{t("On completion the engine consumes materials, creates finished goods, and posts costing & ledger.")}</p>
        </div>
      </Modal>
    </div>
  );
}
